import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function CommentairesPanel({ carte, operateur, onClose }) {
  const [commentaires, setCommentaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nouveauCommentaire, setNouveauCommentaire] = useState('');
  const [typeCommentaire, setTypeCommentaire] = useState('info'); // 'info' ou 'attention'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (carte?.id) {
      chargerCommentaires();
      subscribeToCommentaires();
    }
  }, [carte?.id]);

  const chargerCommentaires = async () => {
    try {
      const { data, error } = await supabase
        .from('commentaires_production')
        .select(`
          *,
          operateurs!operateur_id (
            prenom
          )
        `)
        .eq('carte_id', carte.id)
        .order('date_creation', { ascending: false });

      if (error) throw error;
      
      // Enrichir les commentaires avec le prénom de l'opérateur
      const commentairesEnrichis = (data || []).map(c => ({
        ...c,
        prenom_operateur: c.operateurs?.prenom || c.operateur_nom // Fallback sur operateur_nom si pas de prénom
      }));
      
      setCommentaires(commentairesEnrichis);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToCommentaires = () => {
    const channel = supabase
      .channel(`commentaires-${carte.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'commentaires_production',
          filter: `carte_id=eq.${carte.id}`
        },
        () => {
          chargerCommentaires();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const ajouterCommentaire = async () => {
    if (!nouveauCommentaire.trim() || !operateur) return;

    setSubmitting(true);
    try {
      const commentaireData = {
        carte_id: carte.id,
        operateur_nom: operateur.nom || operateur.prenom + ' ' + operateur.nom || 'Opérateur',
        type_commentaire: typeCommentaire,
        contenu: nouveauCommentaire.trim()
      };

      // Ajouter operateur_id seulement si ce n'est pas l'UUID fictif de l'admin
      if (operateur.id && operateur.id !== '00000000-0000-0000-0000-000000000000') {
        commentaireData.operateur_id = operateur.id;
      }

      const { data, error } = await supabase
        .from('commentaires_production')
        .insert(commentaireData)
        .select();

      if (error) throw error;

      setNouveauCommentaire('');
      setTypeCommentaire('info');
      
      await chargerCommentaires();
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      alert(`Erreur lors de l'ajout du commentaire: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeConfig = (type) => {
    const configs = {
      info: { emoji: '💬', label: 'Info', color: '#1aabf9', bg: '#e7f5ff' },
      attention: { emoji: '⚠️', label: 'Attention', color: '#f59e0b', bg: '#fff3cd' }
    };
    return configs[type] || configs.info;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '450px',
      backgroundColor: '#ffffff',
      boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header - Optimisé */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e0e5ea',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '8px'
        }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#212529',
            margin: 0
          }}>
            💬 Commentaires
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#6c757d',
              transition: 'color 0.2s',
              lineHeight: 1
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#212529'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
          >
            ✕
          </button>
        </div>
        <div style={{
          fontSize: '13px',
          color: '#495057',
          lineHeight: '1.4'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '2px' }}>
            {carte?.nom_attribue || carte?.types_produits?.nom}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {carte?.types_produits?.nom} • Semaine {carte?.semaine_planifiee}
          </div>
        </div>
      </div>

      {/* Liste des commentaires - Maximisée */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            Chargement...
          </div>
        ) : commentaires.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>💭</div>
            <p style={{ margin: 0 }}>Aucun commentaire</p>
            <p style={{ fontSize: '13px', marginTop: '4px' }}>Soyez le premier à commenter !</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {commentaires.map(commentaire => {
              const typeConfig = getTypeConfig(commentaire.type_commentaire);
              const reactions = commentaire.reactions || { thumbs_up: 0, thumbs_down: 0, heart: 0 };

              return (
                <div
                  key={commentaire.id}
                  style={{
                    padding: '12px',
                    backgroundColor: typeConfig.bg,
                    borderRadius: '8px',
                    borderLeft: `3px solid ${typeConfig.color}`
                  }}
                >
                  {/* Header du commentaire */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '4px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: typeConfig.color,
                          backgroundColor: '#ffffff',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          {typeConfig.emoji} {typeConfig.label}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d'
                      }}>
                        <strong>{commentaire.prenom_operateur}</strong> • {formatDate(commentaire.date_creation)}
                      </div>
                    </div>
                  </div>

                  {/* Contenu */}
                  <div style={{
                    fontSize: '14px',
                    color: '#212529',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {commentaire.contenu}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Formulaire nouveau commentaire - Optimisé */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid #e0e5ea',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['info', 'attention'].map(type => {
              const config = getTypeConfig(type);
              const isSelected = typeCommentaire === type;
              return (
                <button
                  key={type}
                  onClick={() => setTypeCommentaire(type)}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    backgroundColor: isSelected ? config.color : '#ffffff',
                    color: isSelected ? '#ffffff' : config.color,
                    border: `2px solid ${config.color}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{config.emoji}</span>
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>

        <textarea
          value={nouveauCommentaire}
          onChange={(e) => setNouveauCommentaire(e.target.value)}
          placeholder="Écrivez votre commentaire..."
          disabled={submitting}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '8px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            marginBottom: '8px'
          }}
        />

        <button
          onClick={ajouterCommentaire}
          disabled={!nouveauCommentaire.trim() || submitting}
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: nouveauCommentaire.trim() ? '#1aabf9' : '#e9ecef',
            color: nouveauCommentaire.trim() ? 'white' : '#adb5bd',
            border: 'none',
            borderRadius: '6px',
            cursor: nouveauCommentaire.trim() ? 'pointer' : 'not-allowed',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            if (nouveauCommentaire.trim()) e.currentTarget.style.backgroundColor = '#1590d6';
          }}
          onMouseLeave={(e) => {
            if (nouveauCommentaire.trim()) e.currentTarget.style.backgroundColor = '#1aabf9';
          }}
        >
          {submitting ? 'Envoi...' : '💬 Ajouter un commentaire'}
        </button>
      </div>
    </div>
  );
}

export default CommentairesPanel;
